import { baseV2URL } from './index.js';
import { getToken, refreshAuth } from '@contexts/Token.context';

/**
 * "Find" (proof of concept): text search, "find the rest" from an example, outlines and naming,
 * on `/projects/{project_uuid}/find/`. All geometry is EPSG:3857.
 *
 * Plain fetch rather than apiClientV2: the models take seconds per tile, and the shared client
 * retries a 502/503 with back-off, which would turn "no VLM" into a long wait.
 */

// The renewal under way, shared: several requests answered 401 together (the session poll, a plan
// preview, a run starting) renew the token once, not once each
let renewing = null;
const renewToken = () => {
	renewing ??= Promise.resolve()
		.then(refreshAuth)
		.finally(() => {
			renewing = null;
		});
	return renewing;
};

/**
 * fetch with the user's token; when it has expired (401), renews it once and asks again, as the shared
 * client does for every other request. A 403 is an answer (not an admin), not an expired token.
 */
const authFetch = async (url, { headers, ...init } = {}) => {
	const send = token =>
		fetch(url, { ...init, headers: { ...headers, Authorization: `Bearer ${token}` } });
	const token = getToken();
	const response = await send(token);
	if (response.status !== 401) return response;
	// Renewed meanwhile by another request: only ask again, with the new token
	if (getToken() === token) await renewToken();
	return send(getToken());
};

// A failed request's error, with its HTTP status (422: the area; 403: not allowed; 429: busy)
const requestError = (payload, status) =>
	Object.assign(new Error(payload?.message || `Find request failed (${status})`), { status });

// A drawn area goes with the request; the view sends none
const withPolygon = polygon => (polygon ? { polygon } : {});

const findRequest = async (projectUuid, path, body, method = body ? 'POST' : 'GET') => {
	const response = await authFetch(`${baseV2URL}/projects/${projectUuid}/find/${path}`, {
		method,
		headers: body ? { 'Content-Type': 'application/json' } : {},
		body: body && JSON.stringify(body),
	});
	const payload = await response.json().catch(() => null);
	if (!response.ok || payload?.success === false) throw requestError(payload, response.status);
	return payload?.data ?? null;
};

/**
 * The tiles a search of `bounds` would run on, nearest the centre first: `{ tiles, grid,
 * searched_bounds, clamped, tile_m, area_m, sizes, … }`. The squares are sized for `objects`
 * (words: "car", "house") or `groups` (examples: `[{ boxes }]`), about ten objects wide. Runs no
 * detector, so the map can show the area before a search.
 */
export const findPlan = ({
	projectUuid,
	bounds,
	polygon,
	maxTiles = MAX_SEARCH_TILES,
	objects,
	groups,
}) =>
	findRequest(projectUuid, 'plan', {
		bounds,
		...withPolygon(polygon),
		max_tiles: maxTiles,
		objects,
		groups,
	});

/** The most tiles one streamed search covers; a larger view must be zoomed into or drawn smaller. */
export const MAX_SEARCH_TILES = 10000;

/**
 * A streamed answer (`find/{path}/stream`), each event of its newline-delimited JSON to `onEvent`;
 * resolves with the `done` event, an `error` event or a cut-off stream rejects. Plain fetch with a
 * reader: EventSource cannot send the Authorization header.
 */
const findStream = async (projectUuid, path, body, { onEvent, signal }) => {
	const response = await authFetch(`${baseV2URL}/projects/${projectUuid}/find/${path}/stream`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
		signal,
	});
	const last = await readEvents(response, onEvent);
	if (last?.type !== 'done') throw new Error('The search stopped before it finished');
	return last;
};

// One line of the answer; a line that is not JSON is the server's fault, said plainly. `final`: asking
// again would not help (the page does not resume after it)
const parseEvent = line => {
	try {
		return JSON.parse(line);
	} catch {
		throw Object.assign(new Error('The search sent a malformed answer'), { final: true });
	}
};

/**
 * Hands each event of a newline-delimited JSON answer to `onEvent`, and resolves with the last one.
 * Keep-alives (`ping`, every 15 s while a square takes long) are skipped. An `error` event (`final`:
 * the run failed, asking again would not help) or a failed request (with its `status`) rejects; so
 * does an abort or a cut-off stream, which also lets go of the stream.
 */
export const readEvents = async (response, onEvent) => {
	if (!response.ok) {
		const payload = await response.json().catch(() => null);
		throw requestError(payload, response.status);
	}
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let last = null;
	const handle = line => {
		if (!line.trim()) return;
		const event = parseEvent(line);
		if (event.type === 'ping') return;
		if (event.type === 'error') {
			throw Object.assign(new Error(event.message || 'The search failed'), { final: true });
		}
		onEvent(event);
		last = event;
	};
	try {
		for (;;) {
			const { value, done } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split('\n');
			buffer = lines.pop();
			lines.forEach(handle);
		}
		// What the decoder still holds: the end of a character split over the last two chunks
		handle(buffer + decoder.decode());
	} catch (error) {
		reader.cancel().catch(() => null);
		throw error;
	}
	return last;
};

/**
 * Start a search the server runs on after this page goes away (another tab of the app, a reload):
 * `kind` is query, similar, rest or model, `body` what its `/stream` route takes. Resolves with
 * `{ uuid, plan }`, the plan being its first event. A query naming only continuous things (roads,
 * rivers, lakes) runs in segment mode: its plan says `mode: 'segment'`, its squares send traced
 * areas, and `done` the stitched areas and centrelines with `stats` ({ length_km, area_ha, pieces,
 * gaps_bridged }). Words mixing those with objects are refused (422).
 */
export const startFindRun = ({ projectUuid, kind, body }) =>
	findRequest(projectUuid, `${kind}/runs`, body);

/**
 * A run's events after `after` (-1: all of them, the plan first), then its new ones as they come,
 * each with its `seq`, until it ends with `done` or `stopped`. A run still going sends `caught_up`
 * once, after the events it had already. Leaving (aborting `signal`) stops only the following, not
 * the run.
 */
export const followFindRun = async ({ projectUuid, runUuid, after = -1 }, { onEvent, signal }) => {
	const response = await authFetch(
		`${baseV2URL}/projects/${projectUuid}/find/runs/${runUuid}/events?after=${after}`,
		{ signal }
	);
	const last = await readEvents(response, onEvent);
	if (!['done', 'stopped'].includes(last?.type)) {
		throw new Error('The search stopped before it finished');
	}
	return last;
};

/**
 * The user's newest run in the project, `{ uuid, kind, status, request, … }` (`kind` segment for a
 * traced query), or null when there is
 * none or it was cleared.
 */
export const getLatestFindRun = async ({ projectUuid }) => {
	// With no run, `data` is null
	const latest = await findRequest(projectUuid, 'runs/latest');
	return latest?.uuid ? latest : null;
};

/** Stop a run at its next square; what it found stays. */
export const stopFindRun = ({ projectUuid, runUuid }) =>
	findRequest(projectUuid, `runs/${runUuid}/stop`, {});

/** Stop a run and clear it, so the next page opening Find does not pick it up. */
export const clearFindRun = ({ projectUuid, runUuid }) =>
	findRequest(projectUuid, `runs/${runUuid}`, undefined, 'DELETE');

/**
 * The bodies of the searches, for their `/stream` and `/runs` routes alike. `polygon`: a drawn area's
 * ring (EPSG:3857); the whole squares it touches run, and only what is inside it is kept.
 */
export const queryBody = ({ text, bounds, polygon, maxTiles }) => ({
	text,
	bounds,
	...withPolygon(polygon),
	max_tiles: maxTiles,
});
export const similarBody = ({ groups, bounds, polygon, maxTiles = MAX_SEARCH_TILES }) => ({
	// One entry per object, each with its own examples and name ("car", "house")
	groups: groups.map(({ boxes, name }) => ({ boxes, ...(name && { name }) })),
	bounds,
	...withPolygon(polygon),
	max_tiles: maxTiles,
});
export const modelBody = ({ modelUuid, bounds, polygon }) => ({
	model_uuid: modelUuid,
	bounds,
	...withPolygon(polygon),
});

/**
 * The trained orthophoto models the user may run: `{ models: [{ model_uuid, name, classes,
 * basemodel, hover_description, runs }] }`, those Find can run first (`runs`: BFM models for now).
 */
export const getFindModels = ({ projectUuid }) => findRequest(projectUuid, 'models');

/**
 * What a search of the typed text runs, our models first: `{ words, models: [{ model_uuid, name,
 * basemodel, runs, classes, words, fit }], generic_words, top }`. `top` is "model" when the first
 * model fits every word ("trees", "trær": Tree Finder), else "generic". Runs nothing, so it can be
 * asked as the user types.
 */
export const findRoute = ({ projectUuid, text }) => findRequest(projectUuid, 'route', { text });

/** "Outline all": SAM 3's outline of the object in each box, streamed in the order sent. */
export const streamOutlines = ({ projectUuid, boxes }, handlers) =>
	findStream(projectUuid, 'outline', { boxes }, handlers);

/**
 * What a typed request asks to find: Claude reads a sentence in any language ("Find all cars and
 * houses here") as detector words; a plain list of words is split without Claude.
 * @returns {Promise<{objects: string[], source: 'claude'|'words'}>}
 */
export const findParse = ({ projectUuid, text }) => findRequest(projectUuid, 'parse', { text });

/** The outline of the object at a point or in a box. */
export const findSegment = ({ projectUuid, point, box }) =>
	findRequest(projectUuid, 'segment', { ...(point && { point }), ...(box && { box }) });

/**
 * What the project's orthophoto holds, most common first: `{ objects: [{ name, how_many, length_m,
 * width_m }], views }`. Claude looks at sample views (no GPU); about 10 s the first time.
 */
export const getFindObjects = ({ projectUuid }) => findRequest(projectUuid, 'objects');

/** What the object in a box is: `{ label, confidence, natural, why }`. */
export const findIdentify = ({ projectUuid, box }) => findRequest(projectUuid, 'identify', { box });

/**
 * Which models run: `{ backend, state, vlm, instance_type, started_at }`. `state` is none, stopped,
 * starting, ready, stopping or failed. Asking is not use: compute stops itself 10 minutes after the
 * last search, also while the Find panel stays open.
 */
export const getFindSession = ({ projectUuid }) => findRequest(projectUuid, 'session');

/** Start compute (the GPU worker); it stops itself when idle. */
export const startFindSession = ({ projectUuid }) => findRequest(projectUuid, 'session', {});

/** Stop compute now. */
export const stopFindSession = ({ projectUuid }) =>
	findRequest(projectUuid, 'session', undefined, 'DELETE');

/**
 * What the next search would cost, before it runs: `{ plan: { squares, tile_m, area_m }, estimate:
 * { usd_total, usd_startup, usd_compute, usd_llm, seconds_estimate, currency, breakdown } }`. `body`
 * from `estimateBody` (findCost.js); `kind` segment for words naming only roads, rivers…. Runs nothing.
 */
export const findEstimate = ({ projectUuid, body }) => findRequest(projectUuid, 'estimate', body);

/**
 * Save a finished (or stopped) run's results to the project as a job, without the boxes in `drop`
 * (feature ids the user removed): `{ job_uuid, count, labels, nodes, cost, collection }`. Saving
 * again gives the same job. With `collectionUuid` each label naming one of the collection's objects
 * is saved as that object, inside the searched area, and the area joins the collection's analyzed
 * area (`collection`: `{ uuid, name, nodes, unmatched, empty, count, replaced }`);
 * `collectionNodes` (label → node uuid) names the object of a label whose name does not match.
 */
export const saveFindRun = ({
	projectUuid,
	runUuid,
	name,
	drop = [],
	collectionUuid,
	collectionNodes,
}) =>
	findRequest(projectUuid, `runs/${runUuid}/save`, {
		...(name && { name }),
		drop,
		...(collectionUuid && { collection_uuid: collectionUuid }),
		...(collectionNodes && { collection_nodes: collectionNodes }),
	});

/** The project's saved Find results: `{ saved: [{ job_uuid, name, created_at, count, labels, cost, collection }] }`. */
export const getSavedFinds = ({ projectUuid }) => findRequest(projectUuid, 'saved');

/**
 * The collections Find offers in the project, analyzed ones first: `{ collections: [{ uuid, name,
 * objects: [{ node_uuid, name, count }], searches: [{ text, mode, names }], analyzed, total,
 * find_saves, last_find_at }] }`. `searches` are what to type to find its objects; roads and the
 * like (`mode` segment) are a search of their own.
 */
export const getFindCollections = ({ projectUuid }) => findRequest(projectUuid, 'collections');

/** One saved Find result's boxes, as a FeatureCollection (EPSG:3857). */
export const getSavedFind = ({ projectUuid, jobUuid }) =>
	findRequest(projectUuid, `saved/${jobUuid}`);

/** Delete a saved Find result. */
export const deleteSavedFind = ({ projectUuid, jobUuid }) =>
	findRequest(projectUuid, `saved/${jobUuid}`, undefined, 'DELETE');

/** What Find has cost in the project: `{ rows, month_usd }`. */
export const getFindUsage = ({ projectUuid }) => findRequest(projectUuid, 'usage');
